import apiService from './apiService';
import {
  InvoiceSummaryData,
  IncompleteInvoice,
  VoucherItem,
  SpecializedVoucherItem,
  TaskItem,
  TaskFormData,
  WidgetData
} from '../types/dashboard';

// Helper function to get branch ID header
const getBranchHeaders = () => {
  const activeBranchId = localStorage.getItem('activeBranchId');
  return activeBranchId ? { 'x-branch-id': activeBranchId } : {};
};

// Get invoice summary data
export const getInvoiceSummary = async (
  period: 'day' | 'week' | 'month' = 'day',
  branchId?: string
): Promise<InvoiceSummaryData> => {
  try {
    const params: any = { period };
    if (branchId) {
      params.branchId = branchId;
    }
    
    return await apiService.get<InvoiceSummaryData>('/dashboard/invoice-summary', {
      params,
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching invoice summary:', error);
    throw error;
  }
};

// Get incomplete invoices
export const getIncompleteInvoices = async (): Promise<IncompleteInvoice[]> => {
  try {
    return await apiService.get<IncompleteInvoice[]>('/dashboard/incomplete-invoices', {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching incomplete invoices:', error);
    throw error;
  }
};

// Get incomplete vouchers (receipts and payments)
export const getIncompleteVouchers = async (type?: 'receipt' | 'payment'): Promise<VoucherItem[]> => {
  try {
    const params = type ? { type } : {};
    return await apiService.get<VoucherItem[]>('/dashboard/incomplete-vouchers', {
      params,
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching incomplete vouchers:', error);
    throw error;
  }
};

// Get incomplete specialized vouchers
export const getIncompleteSpecializedVouchers = async (): Promise<SpecializedVoucherItem[]> => {
  try {
    return await apiService.get<SpecializedVoucherItem[]>('/dashboard/incomplete-specialized-vouchers', {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching incomplete specialized vouchers:', error);
    throw error;
  }
};

// Get user tasks
export const getTasks = async (): Promise<TaskItem[]> => {
  try {
    return await apiService.get<TaskItem[]>('/dashboard/tasks', {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

// Create a new task
export const createTask = async (taskData: TaskFormData): Promise<TaskItem> => {
  try {
    return await apiService.post<TaskItem>('/dashboard/tasks', taskData, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

// Update task
export const updateTask = async (taskId: string, taskData: Partial<TaskFormData>): Promise<TaskItem> => {
  try {
    return await apiService.patch<TaskItem>(`/dashboard/tasks/${taskId}`, taskData, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

// Toggle task status
export const toggleTaskStatus = async (taskId: string): Promise<TaskItem> => {
  try {
    return await apiService.patch<TaskItem>(`/dashboard/tasks/${taskId}/toggle`, {}, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error toggling task status:', error);
    throw error;
  }
};

// Delete task
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    await apiService.delete(`/dashboard/tasks/${taskId}`, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// Get all widget data at once
export const getAllWidgetData = async (): Promise<WidgetData> => {
  try {
    return await apiService.get<WidgetData>('/dashboard/widgets', {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching widget data:', error);
    throw error;
  }
};

// Update user dashboard widgets
export const updateUserWidgets = async (widgets: string[]): Promise<void> => {
  try {
    await apiService.patch('/dashboard/user-widgets', { widgets }, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error updating user widgets:', error);
    throw error;
  }
};

// Admin: Get all users with their widgets
export const getAllUsersWidgets = async (): Promise<any[]> => {
  try {
    return await apiService.get<any[]>('/dashboard/admin/users-widgets', {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error fetching users widgets:', error);
    throw error;
  }
};

// Admin: Update specific user's widgets
export const updateUserWidgetsByAdmin = async (userId: string, widgets: string[]): Promise<void> => {
  try {
    await apiService.patch(`/dashboard/admin/users/${userId}/widgets`, { widgets }, {
      headers: getBranchHeaders()
    });
  } catch (error) {
    console.error('Error updating user widgets by admin:', error);
    throw error;
  }
};